import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { fetchLocalizedCollection } from '../lib/cms';

export default function SparePartsPage() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'da').then((items) => {
      const getSlug = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || Object.values(it.slug)[0]
          : it?.slug;
      const match = items.find((s) => getSlug(s) === 'reservedele');
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Originale reservedele til PMB og industrimaskiner';
  const badge = pageData?.undertitel || 'Originale Komponenter';
  const intro =
    pageData?.intro ||
    'Når en maskine kræver udskiftning af sliddele eller komponenter, er pasform og kvalitet afgørende for oppetid og præcision. Vi lagerfører et omfattende sortiment af originale reservedele til PMB excenterpresser, hydraulik, centralsmøring og pladebearbejdning.';
  const seoTitle = pageData?.seo_titel || 'PMB Reservedele – Originale reservedele til presser og maskiner';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'Originale reservedele til PMB presser og tilknyttede maskiner. Hurtig levering af koblinger, pakninger, filtre, styringer og sliddele.';
  const image =
    pageData && pageData.billede
      ? Array.isArray(pageData.billede)
        ? pageData.billede[0]
        : pageData.billede
      : 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/reservedele.png';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={intro} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Produkter</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Reservedele</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
                {badge}
              </span>
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {title}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
                {intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-4 text-sm font-semibold">
                <a
                  href="#bestil"
                  className="bg-[#006368] hover:bg-[#005155] text-white px-6 py-3 rounded-lg shadow-sm transition"
                >
                  Forespørg på reservedele
                </a>
                <a
                  href="tel:+4557526262"
                  className="bg-stone-800 hover:bg-stone-700 text-stone-200 px-6 py-3 rounded-lg border border-stone-700 transition flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone" /> 57 52 62 62
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-stone-700 shadow-2xl bg-stone-800">
                <img
                  src={image}
                  alt="PMB Reservedele"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories of spare parts */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900">
              Hvad kan vi levere?
            </h2>
            <p className="mt-2 text-stone-600 text-sm sm:text-base">
              Med vores lange historik og eget maskinværksted i Borup kan vi både levere hyldevarer og
              fremstille specialdele efter tegning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-gear" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Pressekomponenter</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Koblinger, bremsebelægninger, stødskruer, plejlstænger, bøsninger og lejer til PMB presser.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-droplet" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Smøresystemer</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Pumper, fordelere, slanger, forskruninger, overvågningssensorer og smøremidler fra SKF m.fl.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-microchip" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Styring & El</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Sikkerhedsrelæer, tohåndsbetjeninger, sensorer, encodere og styringskomponenter.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-screwdriver-wrench" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Specialfremstilling</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Drejning, fræsning og tilpasning af udgåede maskindele på vores eget maskinværksted.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="bestil" className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-50 rounded-2xl border border-stone-200 p-8 sm:p-12">
            <div className="text-center max-w-xl mx-auto mb-8">
              <span className="text-xs font-bold text-[#006368] uppercase tracking-wider">
                Hurtig ekspedition
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 mt-2">
                Forespørg på reservedele
              </h2>
              <p className="text-sm text-stone-600 mt-2">
                Angiv gerne maskinens model, årgang, serienummer eller reservedelsnummer for hurtigst mulig betjening.
              </p>
            </div>

            <ContactForm submitButtonText="Send reservedelsforespørgsel" />
          </div>
        </div>
      </section>
    </>
  );
}
